import { Suspense, createContext, lazy, useCallback, useContext, useMemo, useState } from 'react';
import { notifyAiLimit } from '@/shared/utils/apiError';
import type { ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import type { ScanResult } from '@/features/onboarding/types';
import { showSuccessMessage } from '@/shared/adapters/toast';
import { _t } from '@/i18n/t';
import { rescanCompareKey } from './hooks/useRescanCompare';
import { notifyRescanRefused } from './utils/notifyRescanRefused';
import { visibilityReportKey } from '@/features/visibility-report/utils/visibilityReportKeys';
import { estimatePctFromCompletedAgents } from '@/features/onboarding/utils/crawlProgress';
import { mediaListKey } from '@/features/media/hooks/useMediaList';

const CrawlStatus = lazy(() => import('@/features/onboarding/pages/CrawlStatus'));

interface RescanScanState {
  companyId: number;
  scanId: string;
  rescanDbId: number;
  websiteUrl: string;
  companyName: string;
  hasPrevious: boolean;
  completedAgents?: string[];
}

export type RescanOutcome = 'completed' | 'failed';

interface RescanControllerValue {
  isScanning: boolean;
  /** The scan has finished and its result is being saved, which includes the site health check. */
  isSaving: boolean;
  scanPct: number;
  activeCompanyId: number | null;
  activeRescanDbId: number | null;
  startRescan: (companyId: number, hasPrevious?: boolean) => Promise<void>;
  /** Takes over a scan the server already has running, e.g. one found after a reload. */
  adoptRunning: (scan: RescanScanState) => void;
  cancelRescan: () => void;
  /** The server has recorded the running scan as finished, so the page stops waiting on the stream. */
  settleRescan: (outcome: RescanOutcome) => void;
}

const RescanControllerContext = createContext<RescanControllerValue | null>(null);

export const RescanControllerProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();
  const [scanState, setScanState] = useState<RescanScanState | null>(null);
  const [scanPct, setScanPct] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [minimized, setMinimized] = useState(true);
  const [startingCompanyId, setStartingCompanyId] = useState<number | null>(null);

  const isScanning = scanState !== null || startingCompanyId !== null;

  const reset = useCallback(() => {
    setScanState(null);
    setScanPct(0);
    setIsSaving(false);
    setMinimized(true);
  }, []);

  const startRescan = useCallback(async (companyId: number, hasPrevious = false) => {
    if (isScanning) return;
    setStartingCompanyId(companyId);
    try {
      const res = await fetch('/api/ui/rescans/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ companyId }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        if (notifyAiLimit(body) || notifyRescanRefused(body)) return;
        throw new Error(`Start failed: ${res.status}`);
      }
      const data = await res.json() as { scanId: string; rescanDbId: number; websiteUrl: string; companyName: string };
      setScanPct(0);
      setMinimized(true);
      setScanState({ companyId, hasPrevious, ...data });
    } catch (err) {
      console.error('[rescan] start error:', err);
    } finally {
      setStartingCompanyId(null);
    }
  }, [isScanning]);

  const adoptRunning = useCallback((scan: RescanScanState) => {
    if (isScanning) return;
    setScanState(scan);
    setScanPct(estimatePctFromCompletedAgents(scan.completedAgents ?? [], true));
  }, [isScanning]);

  const handleScanComplete = useCallback(async (result: ScanResult) => {
    if (!scanState) return;
    setIsSaving(true);
    try {
      // Awaits the full response, including the site health audit the backend
      // now runs before replying — CrawlStatus holds off closing until this settles.
      await fetch('/api/ui/rescans/complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ rescanDbId: scanState.rescanDbId, result }),
      });
    } catch (err) {
      console.error('[rescan] complete error:', err);
    }
  }, [scanState]);

  const handleScanDone = useCallback(() => {
    if (!scanState) return;
    const { companyId, hasPrevious } = scanState;
    reset();
    queryClient.invalidateQueries({ queryKey: rescanCompareKey(companyId) });
    queryClient.invalidateQueries({ queryKey: visibilityReportKey(companyId) });
    queryClient.invalidateQueries({ queryKey: mediaListKey(companyId) });
    showSuccessMessage(hasPrevious ? 'Rescan completed' : 'Scan completed');
  }, [scanState, queryClient, reset]);

  const settleRescan = useCallback((outcome: RescanOutcome) => {
    if (outcome === 'completed') handleScanDone();
    else reset();
  }, [handleScanDone, reset]);

  const handleCancel = useCallback(() => {
    const scanId = scanState?.scanId;
    const rescanDbId = scanState?.rescanDbId;
    if (scanId) {
      fetch(`/api/ui/scans/${scanId}/abort`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      }).catch((err) => console.error('[rescan] abort error:', err));
    }
    if (rescanDbId) {
      fetch('/api/ui/rescans/fail', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ rescanDbId, error: 'Rescan cancelled' }),
      }).catch((err) => console.error('[rescan] fail error:', err));
    }
    reset();
  }, [scanState, reset]);

  const handleScanError = useCallback(() => {
    const rescanDbId = scanState?.rescanDbId;
    if (rescanDbId) {
      fetch('/api/ui/rescans/fail', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ rescanDbId, error: 'Rescan failed' }),
      }).catch((err) => console.error('[rescan] fail error:', err));
    }
    reset();
  }, [scanState, reset]);

  const value = useMemo<RescanControllerValue>(() => ({
    isScanning,
    isSaving,
    scanPct,
    activeCompanyId: scanState?.companyId ?? startingCompanyId,
    activeRescanDbId: scanState?.rescanDbId ?? null,
    startRescan,
    adoptRunning,
    cancelRescan: handleCancel,
    settleRescan,
  }), [isScanning, isSaving, scanPct, scanState, startingCompanyId, startRescan, adoptRunning, handleCancel, settleRescan]);

  return (
    <RescanControllerContext.Provider value={value}>
      {children}
      {scanState && (
        <>
          {minimized && (
            <button
              type="button"
              className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2.5 bg-card border border-border rounded-full px-4 py-2 shadow-lg hover:border-primary/50 transition-colors cursor-pointer !bg-white"
              data-testid="rescan-pill"
              onClick={() => setMinimized(false)}
            >
              <Loader2 className="w-3.5 h-3.5 animate-spin text-primary shrink-0" />
              <span className="text-[13px] font-medium text-foreground">{_t('rescan.pill.inProgress')}</span>
              <span className="text-[13px] text-muted-foreground tabular-nums">
                {isSaving ? _t('rescan.saving') : `${Math.round(scanPct)}%`}
              </span>
            </button>
          )}
          <Suspense fallback={null}>
            <CrawlStatus
              name={scanState.companyName}
              url={scanState.websiteUrl}
              scanId={scanState.scanId}
              rescan
              hasPrevious={scanState.hasPrevious}
              minimized={minimized}
              initialPct={scanPct}
              initialCompletedAgents={scanState.completedAgents}
              onPctChange={setScanPct}
              onScanComplete={handleScanComplete}
              onDone={handleScanDone}
              onError={handleScanError}
              onCancel={handleCancel}
              onRunInBackground={() => setMinimized(true)}
            />
          </Suspense>
        </>
      )}
    </RescanControllerContext.Provider>
  );
};

export const useRescanController = () => {
  const ctx = useContext(RescanControllerContext);
  if (!ctx) {
    throw new Error('useRescanController must be used within RescanControllerProvider');
  }
  return ctx;
};
